import React from 'react';

import { Bars3BottomRightIcon, MagnifyingGlassIcon, GiftIcon } from '@heroicons/react/24/outline';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { useIntl } from 'react-intl';

import { isMobile } from 'react-device-detect';
import { AbsoluteNavLink } from '@/components/AbsoluteNavLink';
import GameIcon from '../UI/Icons/GameIcon';
import SmallLogo from '../UI/SmallLogo';
import NotificationBadge from '@/components/UI/NotificationBadge';
import { openSearchPallete } from '@/utils/utils';
import { GTagEvents, triggerGTag } from '@/lib/gTagManager';
import { openDrawer } from '@/store/ui/actions';
import { DrawerName } from '@/store/ui/reducers';
import { selectUiIsSingleGame } from '@/store/ui/selectors';
import { selectAvailableBonuses } from '@/store/bonuses/selectors';
import { selectLocale } from '@/store/selectors/commonSelectors';
import { useModal } from '@/features/modal/useModal';
import { MODALS } from '@/features/modal/types';
import { isRegulatedLocale } from '@/shared/utility';

const StyledTabBar = styled.div`
  bottom: 0;
  right: 0;
  left: 0;
  background: ${props => props.theme.colors.white};
  justify-content: space-around;
  align-items: center;
  border-top: 1px solid #eee;
  z-index: 90;
  padding-bottom: var(--safe-area-inset-bottom, 0px);

  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    top: 100%;
    height: 50px;
    background: inherit;
  }

  a,
  button {
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-size: 10px;
    padding: 10px 4px;
    border: none;
    background: ${props => props.theme.colors.white};
    justify-content: center;
    align-items: center;
    text-decoration: none;
    color: ${props => props.theme.colors.blue3};
    flex: 1;
    font-weight: bold;
    line-height: 1.2;
    position: relative;
    cursor: pointer;

    &.active {
      color: ${props => props.theme.colors.blue1};

      svg * {
        stroke: ${props => props.theme.colors.blue1};
      }
    }

    svg {
      width: 24px;
      height: 24px;
    }
  }

  .tab-bar__label {
    font-size: 10px;
    font-weight: bold;
    line-height: 1.2;
    white-space: nowrap;
  }
`;

function TabBar(props) {
  const onNavigate = props?.onNavigate;
  const intl = useIntl();
  const isSingleGame = useSelector(selectUiIsSingleGame);
  const isAuthenticated = useSelector(state => state.auth.token !== null);
  const availableBonuses = useSelector(selectAvailableBonuses);
  const locale = useSelector(selectLocale);
  const dispatch = useDispatch();
  const { openModal } = useModal();

  if (isSingleGame && isMobile && !onNavigate) {
    return null;
  }

  const showBonuses = isAuthenticated && availableBonuses.length > 0 && !isRegulatedLocale(locale);

  const handleBonusesClick = () => {
    triggerGTag(GTagEvents.bottom_nav_bonuses_click);
    openModal(MODALS.AVAILABLE_BONUSES, { bonuses: availableBonuses });
  };

  const handleSearchClick = () => {
    triggerGTag(GTagEvents.bottom_nav_search_click);
    openSearchPallete();
  };

  const handleMenuClick = () => {
    triggerGTag(GTagEvents.bottom_nav_menu_click);
    dispatch(openDrawer(DrawerName.Menu));
  };

  return (
    <StyledTabBar className="flex fixed lg:relative lg:hidden" id="thumb-nav">
      <AbsoluteNavLink
        to="/"
        aria-label="LuckyDays"
        onClick={e => {
          if (onNavigate && onNavigate('/')) {
            e.preventDefault();
            return;
          }
          triggerGTag(GTagEvents.bottom_nav_home_click);
        }}
        end
      >
        <SmallLogo />
        <span className="tab-bar__label">{intl.formatMessage({ id: 'header.home' })}</span>
      </AbsoluteNavLink>
      <AbsoluteNavLink
        to="/games"
        aria-label="Games"
        onClick={e => {
          if (onNavigate && onNavigate('/games')) {
            e.preventDefault();
            return;
          }
          triggerGTag(GTagEvents.bottom_nav_games_click);
        }}
      >
        <GameIcon />
        <span className="tab-bar__label">{intl.formatMessage({ id: 'header.games' })}</span>
      </AbsoluteNavLink>
      {showBonuses && (
        <button
          data-cy="tab-bar-bonuses-button"
          type="button"
          aria-label="Available bonuses"
          onClick={handleBonusesClick}
        >
          <div className="relative">
            <NotificationBadge count={availableBonuses.length} />
            <GiftIcon />
          </div>
          <span className="tab-bar__label">{intl.formatMessage({ id: 'misc.bonuses' })}</span>
        </button>
      )}
      <button
        type="button"
        title="Search game"
        aria-label="Search game"
        onClick={handleSearchClick}
      >
        <MagnifyingGlassIcon strokeWidth={2} />
        <span className="tab-bar__label">{intl.formatMessage({ id: 'games.search' })}</span>
      </button>
      <button
        data-cy="tab-bar-menu-button"
        type="button"
        title="Menu"
        aria-label="Menu"
        onClick={handleMenuClick}
      >
        <Bars3BottomRightIcon strokeWidth={2} />
        <span className="tab-bar__label">{intl.formatMessage({ id: 'nav.more' })}</span>
      </button>
    </StyledTabBar>
  );
}

export default TabBar;
